import React from "react";
import moment from "moment";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { Pie } from "@nivo/pie";
import { Utils } from "@kido/plots";
import { reportIDSelector } from "../../store/selectors";

const colors = [
  "ff2f92",
  "7030a0",
  "a8dadcf",
  "457b9df",
  "1d3557f",
  "ee6c4d",
  "4361ee",
  "3a0ca3",
  "90be6d",
  "4cc9f0"
];

const Legend = ({ data, colors }) =>
  data.map((d, i) => {
    const color = colors[i];

    return (
      <div className="legend flex m-2">
        <div
          className="legend-item mr-1 w-2 h-2 rounded-sm relative inset-0"
          style={{ background: "#" + color }}
        ></div>
        <div className="legend-item text-sm text-avgStay">{d}</div>
      </div>
    );
  });

const getTitle = (filters) => {
  const base = [
    filters.SEC,
    filters.DIS,
    filters.MUN,
    filters.COM,
    filters.PRO
  ];

  return base.reduce((a, c) => {
    const str = c ? `${c},` : "";
    return `${a}${str}`;
  }, "");
};

const formatPeriod = (dates) => {
  const startDate = dates[0].toLocaleString().split(" ")[0];
  const endDate = dates[1].toLocaleString().split(" ")[0];

  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
};

const formatPreviousPeriod = (dates) => {
  const d = moment(dates[0]);

  return d.subtract(1, "year").format("YYYY");
};

const MainMetrics = ({ filters, url, data, tourist }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [reportID] = useRecoilState(reportIDSelector);
  const topHourDate =
    data.current.topHour &&
    data.current.topHour.length &&
    moment(data.current.topHour[0].time_slice);
  const nationalPieData = data.current.pie.find(
    (x) => x.user_origin === "national"
  );
  const internationalPieData = data.current.pie.find(
    (x) => x.user_origin === "international"
  );
  const prevNationalPieData = data.previous.pie.find(
    (x) => x.user_origin === "national"
  );
  const prevInternationalPieData = data.previous.pie.find(
    (x) => x.user_origin === "international"
  );

  return (
    <div className="page">
      <div className="relative ml-1 w-full p-5 mb-10">
        <div className="flex">
          <div className="text-international mb-2">{t("report")}:</div>
          <div className="ml-2 text-avgStay">
            Turismo en: {getTitle(filters)}
            {t("spain")}
          </div>
        </div>
        <div className="flex">
          <div className="text-international mb-2">{t("requested")}:</div>
          <div className="ml-2 text-avgStay">info@kidodynamics.com</div>
        </div>
        <div className="flex">
          <div className="text-international mb-2">{t("date")}:</div>
          <div className="ml-2 text-avgStay">{new Date().toLocaleString()}</div>
        </div>
        <div className="flex absolute bottom-0 right-0 mr-1">
          <div className="text-international">ID:</div>
          <div className="ml-2 text-avgStay">{reportID}</div>
        </div>
      </div>
      <div className="w-full h-px bg-gray-800" />
      <div className="w-full p-5 mb-10">
        <div className="flex h-40 h400">
          <div className="w-2/5 pr-8">
            <div className="flex justify-between w-full">
              <div className="text-international mb-2 font-bold">
                {t("geo_selection")}
              </div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("country")}:</div>
              <div className="text-avgStay">{t("spain")}</div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("PRO")}:</div>
              <div className="text-avgStay">{filters.PRO || t("all")}</div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("COM")}:</div>
              <div className="text-avgStay">{filters.COM || t("all")}</div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("MUN")}:</div>
              <div className="text-avgStay truncate">
                {filters.MUN || t("all")}
              </div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("DISC")}:</div>
              <div className="text-avgStay">{filters.DISC || t("all")}</div>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="text-avgStay font-bold">{t("pobl")}:</div>
              <div className="text-avgStay">
                {Utils.toThousands(locale, filters.population)}
              </div>
            </div>
          </div>
          <div className="w-3/5">
            <div className="flex justify-between w-full">
              <div className="text-international mb-2 font-bold">
                {t("vis_reference")}
              </div>
            </div>
            <div className="h-full w-full">
              <img src={url} alt="map" />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full h-px bg-gray-800" />
      <div className="relative ml-1 w-full p-5 mb-10">
        <div className="flex h-24 h400">
          <div className="w-full">
            <div className="flex justify-between w-full">
              <div className="text-international mb-2 font-bold">
                {t("dataset_parameters")}
              </div>
            </div>
            <div className="flex">
              <div className="w-1/2">
                <div className="flex">
                  <div className="text-international mb-2 ">{t("period")}:</div>
                  <div className="text-avgStay">
                    {formatPeriod(filters.dates)}
                  </div>
                </div>
                <div className="flex text-xs space-between mt-2">
                  <div className="text-avgStay ml-6 mr-1">
                    <div className="text-xs mx-1 p-1 flex justify-center">
                      <i className="far fa-calendar"></i>{" "}
                      <span className="block ml-1 font-bold">
                        {t("busiest_day")}
                      </span>
                    </div>
                    <div className="text-center">
                      {data.current.topDay.length &&
                        data.current.topDay[0].date}
                    </div>
                    <div className="flex justify-center">
                      <i className="far fa-user color-icon-user mr-2"></i>
                      <span className="block ml-1 font-bold">
                        {data.current.topDay.length &&
                          Utils.toThousands(
                            locale,
                            data.current.topDay[0].uniques__sum
                          )}
                      </span>
                    </div>
                  </div>
                  <div className="text-avgStay ml-6 mr-1">
                    <div className="text-xs mx-1 p-1 flex justify-center">
                      <i className="far fa-calendar"></i>{" "}
                      <span className="block ml-1 font-bold">
                        {t("busiest_time")}
                      </span>
                    </div>
                    <div className="text-center">
                      {data.current.topHour.length &&
                        topHourDate.format("YYYY-MM-DD HH:mm")}
                    </div>
                    <div className="flex justify-center">
                      <i className="far fa-user color-icon-user mr-2"></i>
                      <span className="block ml-1 font-bold">
                        {data.current.topHour.length &&
                          Utils.toThousands(
                            locale,
                            data.current.topHour[0].uniques__sum
                          )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-1/2">
                <div className="flex">
                  <div className="text-international mb-2 ">
                    {t("same_period")}
                  </div>
                  <div className="text-avgStay">
                    {" "}
                    {formatPreviousPeriod(filters.dates)}
                  </div>
                </div>
                <div className="flex text-xs space-between mt-2">
                  {data.previous.topDay.length !== 0 && (
                    <div className="text-xs text-avgStay ml-6 mr-1">
                      <div className="text-xs mx-1 p-1 flex justify-center">
                        <i className="far fa-calendar"></i>{" "}
                        <span className="block ml-1 font-bold">
                          {t("busiest_day")}
                        </span>
                      </div>
                      <div className="text-center">
                        {data.previous.topDay[0].date}
                      </div>
                      <div className="flex justify-center">
                        <i className="far fa-user color-icon-user mr-2"></i>
                        <span className="block ml-1 font-bold">
                          {Utils.toThousands(
                            locale,
                            data.previous.topDay[0].uniques__sum
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {data.previous.topHour.length !== 0 && (
                    <div className="text-avgStay ml-6 mr-1">
                      <div className="text-xs mx-1 p-1 flex justify-center">
                        <i className="far fa-calendar"></i>{" "}
                        <span className="block ml-1 font-bold">
                          {t("busiest_time")}
                        </span>
                      </div>
                      <div className="text-center">
                        {data.previous.topHour[0].date__hour}
                        :00
                      </div>
                      <div className="flex justify-center">
                        <i className="far fa-user  color-icon-user mr-2"></i>
                        <span className="block ml-1 font-bold">
                          {Utils.toThousands(
                            locale,
                            data.previous.topHour[0].uniques__sum
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full h-px bg-gray-800" />
      <div className="w-full p-5 mb-10">
        <div className="flex h-40 h400">
          <div className="divider-50">
            <div className="flex justify-between w-full">
              <div className="text-international mb-2 font-bold">
                {t("main_metrics")}
              </div>
            </div>
            <div className="flex justify-between w-full">
              <div className="w-full p-2 bg-gray-200 text-sm box-border text-avgStay">
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("pop_res")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.current.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.current.counts.find(
                          (x) => x.user_type === "resident"
                        ).uniques__sum
                      )}{" "}
                    (
                    {data.current.counts.find(
                      (x) => x.user_type === "resident"
                    ) &&
                      data.current.counts.find(
                        (x) => x.user_type === "resident"
                      ).rank}
                    )
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("regulars")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.current.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.current.counts.find(
                          (x) => x.user_type === "regular"
                        ).uniques__sum
                      )}{" "}
                    (
                    {data.current.counts.find(
                      (x) => x.user_type === "regular"
                    ) &&
                      data.current.counts.find((x) => x.user_type === "regular")
                        .rank}
                    )
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("total_uniques")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.current.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.current.counts.find(
                          (x) => x.user_type === "visitor"
                        ).uniques__sum
                      )}{" "}
                    (
                    {data.current.counts.find(
                      (x) => x.user_type === "visitor"
                    ) &&
                      data.current.counts.find((x) => x.user_type === "visitor")
                        .rank}
                    )
                  </div>
                </div>

                <div className="flex justify-between w-full">
                  <div className="py-1 text-national pl-4">
                    {t("nationals")}:
                  </div>
                  <div className="py-1 text-national">
                    <i className="far fa-user mr-2"></i>
                    {nationalPieData !== undefined &&
                      Utils.toThousands(
                        locale,
                        nationalPieData.uniques__sum
                      )}{" "}
                    ({nationalPieData && nationalPieData.rank})
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1 text-international pl-4">
                    {t("internationals")}:
                  </div>
                  <div className="py-1 text-international">
                    <i className="far fa-user mr-2"></i>
                    {internationalPieData !== undefined &&
                      Utils.toThousands(
                        locale,
                        internationalPieData.uniques__sum
                      )}{" "}
                    ({internationalPieData && internationalPieData.rank})
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1">Turistas:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {Utils.toThousands(
                      locale,
                      tourist.current.national + tourist.current.international
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="divider-50">
            <div className="radial-metrics">
              <div className="w-50">
                <div className="flex justify-between w-full py-3 px-5">
                  <div className="py-1 relative">
                    {nationalPieData && internationalPieData && (
                      <Pie
                        margin={{
                          top: 10,
                          right: 10,
                          bottom: 10,
                          left: 10
                        }}
                        data={[
                          {
                            value: nationalPieData.uniques__sum,
                            label: nationalPieData.uniques__sum,
                            id: nationalPieData.uniques__sum
                          },
                          {
                            value: internationalPieData.uniques__sum,
                            label: internationalPieData.uniques__sum,
                            id: internationalPieData.uniques__sum
                          }
                        ]}
                        colors={["#7030a0", "#ff2f92"]}
                        borderColor={{
                          from: "color",
                          modifiers: [["darker", 0.2]]
                        }}
                        width={150}
                        height={150}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={1}
                        borderWidth={1}
                        enableRadialLabels={false}
                        slicesLabelsSkipAngle={10}
                        slicesLabelsTextColor="#ffffff"
                        legends={[
                          {
                            anchor: "bottom-left",
                            direction: "column",
                            translateY: 86,
                            itemWidth: 100,
                            itemHeight: 22,
                            itemTextColor: "#999",
                            symbolSize: 18,
                            symbolShape: "circle"
                          }
                        ]}
                      />
                    )}
                  </div>
                  <div className="py-1 capitalize">
                    <Legend
                      colors={colors}
                      data={["International", "National"]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-10"></div>
        <div className="flex h-40 h400">
          <div className="divider-50">
            <div className="flex justify-between w-full">
              <div className="flex">
                <div className="text-international mb-2 mt-8">
                  {t("same_period")}{" "}
                </div>
                <div className="text-avgStay mt-8">
                  {formatPreviousPeriod(filters.dates)}
                </div>
              </div>
            </div>
            <div className="flex justify-between w-full">
              <div className="w-full p-2 bg-gray-200 text-sm box-border text-avgStay">
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("pop_res")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.previous.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.previous.counts.find(
                          (x) => x.user_type === "resident"
                        ).uniques__sum
                      )}
                    (
                    {data.previous.counts.find(
                      (x) => x.user_type === "resident"
                    ) &&
                      data.previous.counts.find(
                        (x) => x.user_type === "resident"
                      ).rank}
                    )
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("regulars")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.previous.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.previous.counts.find(
                          (x) => x.user_type === "regular"
                        ).uniques__sum
                      )}
                    (
                    {data.previous.counts.find(
                      (x) => x.user_type === "regular"
                    ) &&
                      data.previous.counts.find(
                        (x) => x.user_type === "regular"
                      ).rank}
                    )
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1">{t("total_uniques")}:</div>
                  <div className="py-1">
                    <i className="far fa-user color-icon-user mr-2"></i>
                    {data.previous.counts.length &&
                      Utils.toThousands(
                        locale,
                        data.previous.counts.find(
                          (x) => x.user_type === "visitor"
                        ).uniques__sum
                      )}
                    (
                    {data.previous.counts.find(
                      (x) => x.user_type === "visitor"
                    ) &&
                      data.previous.counts.find(
                        (x) => x.user_type === "visitor"
                      ).rank}
                    )
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1 text-national  pl-4">
                    {t("nationals")}:
                  </div>
                  <div className="py-1 text-national">
                    <i className="far fa-user mr-2"></i>
                    {prevNationalPieData !== undefined &&
                      Utils.toThousands(
                        locale,
                        prevNationalPieData.uniques__sum
                      )}{" "}
                    ({prevNationalPieData && prevNationalPieData.rank})
                  </div>
                </div>
                <div className="flex justify-between w-full">
                  <div className="py-1 text-international  pl-4">
                    {t("internationals")}:
                  </div>
                  <div className="py-1 text-international">
                    <i className="far fa-user mr-2"></i>
                    {prevInternationalPieData !== undefined &&
                      Utils.toThousands(
                        locale,
                        prevInternationalPieData.uniques__sum
                      )}{" "}
                    ({prevInternationalPieData && prevInternationalPieData.rank}
                    )
                  </div>
                </div>
                {tourist.previous.national && (
                  <div className="flex justify-between w-full">
                    <div className="py-1">Turistas:</div>
                    <div className="py-1">
                      <i className="far fa-user color-icon-user mr-2"></i>
                      {Utils.toThousands(
                        locale,
                        tourist.previous.national +
                          tourist.previous.international
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="divider-50">
            <div className="flex justify-between w-full"></div>
            <div className="radial-metrics">
              <div className="w-50">
                <div className="flex justify-between w-full py-3 px-5">
                  <div className="py-1 relative">
                    {prevNationalPieData && prevInternationalPieData && (
                      <Pie
                        margin={{
                          top: 10,
                          right: 10,
                          bottom: 10,
                          left: 10
                        }}
                        data={[
                          {
                            value: prevNationalPieData.uniques__sum,
                            label: prevNationalPieData.uniques__sum,
                            id: prevNationalPieData.uniques__sum
                          },
                          {
                            value: prevInternationalPieData.uniques__sum,
                            label: prevInternationalPieData.uniques__sum,
                            id: prevInternationalPieData.uniques__sum
                          }
                        ]}
                        colors={["#7030a0", "#ff2f92"]}
                        borderColor={{
                          from: "color",
                          modifiers: [["darker", 0.2]]
                        }}
                        width={150}
                        height={150}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={1}
                        borderWidth={1}
                        enableRadialLabels={false}
                        slicesLabelsSkipAngle={10}
                        slicesLabelsTextColor="#ffffff"
                        legends={[
                          {
                            anchor: "bottom-left",
                            direction: "column",
                            translateY: 86,
                            itemWidth: 100,
                            itemHeight: 22,
                            itemTextColor: "#999",
                            symbolSize: 18,
                            symbolShape: "circle"
                          }
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainMetrics;
